import {
    FGStack,
    FGStackItem,
    FGTypography,
} from '@fg/design-system';
import GQDateRangePicker from 'components/GQNavbar/GQDateRangePicker';
import { DATE_PICKER_DEFAULT_PRESETS } from '../../constants';
import NewCountryPicker, { NewCountryPickerFill } from 'components/NewCountryPicker/NewCountryPicker';
import { inject } from 'mobx-react';
import { first, size } from 'lodash/fp';
import rootStore, { IMobxRootState } from '../../RootStore';
import { IActiveCountry, IActiveCountryMap, IActiveGroup } from 'interfaces';
import PickerToggler, { PICKER_GAP } from './PickerToggler';
import {
    MultiRiskPickerPreview,
    MultiRiskPickerClearButton,
} from './MultiRiskPickerPreview';
import { RiskPickerIndicators } from 'components/RiskPicker/RiskPickerIndicators';
import { useRef } from 'react';
import { Moment } from 'moment';

type Props = {
    licensedCountriesArr?: IActiveCountry[];
    firstCountry?: number;
    groups?: IActiveGroup[];
    activeGroupId?: string;
    countries?: IActiveCountryMap;
    selectedRisks?: number[];
    minDate?: Moment;
    maxDate?: Moment;
};

export const CountryRiskPicker = inject(
    ({ countryStore, settingsStore, risksStore, chartStore }: IMobxRootState) => {
        return {
            licensedCountriesArr: countryStore.countries.licensedCountriesArr,
            firstCountry: first(countryStore.currentCountryList),
            groups: settingsStore.activeGroups,
            activeGroupId: settingsStore.activeGroupId,
            countries: countryStore.countries.countries,
            selectedRisks: risksStore.currentList,
            minDate: chartStore?.baseDate
                ?.clone()
                ?.startOf('day')
                ?.subtract(chartStore?.rangeLeft, 'days'),
            maxDate: chartStore?.baseDate
                ?.clone()
                ?.startOf('day')
                ?.add(chartStore?.rangeRight, 'days'),
        };
    }
)((props: Props) => {
    const {
        licensedCountriesArr,
        firstCountry,
        groups,
        countries,
        selectedRisks,
        minDate,
        maxDate
    } = props;

    const riskPickerRef = useRef(null)
    return (
        <FGStack direction="column" style={{ width: '100%' }}>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Country
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <PickerToggler
                            label={
                                countries[firstCountry]?.name ||
                                'Select Country'
                            }
                            icon="Navigator">
                            <NewCountryPickerFill top={135}>
                                <NewCountryPicker
                                    canSelectAllCountries={false}
                                    countriesList={licensedCountriesArr}
                                    selectedCountriesList={[firstCountry]}
                                    groups={groups}
                                    isMultiSelect={false}
                                    onCountrySelected={(countryId: number) => {
                                        rootStore.countryStore.changeFirstCountry(
                                            countryId
                                        );
                                    }}
                                />
                            </NewCountryPickerFill>
                        </PickerToggler>
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Date
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <GQDateRangePicker
                            initialStartDate={minDate.toDate()}
                            initialEndDate={maxDate.toDate()}
                            allowPresetSelectionOutOfRange={true}
                            presets={DATE_PICKER_DEFAULT_PRESETS}
                            onChange={rootStore.chartStore.setNewRangeByDates}
                        />
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Risk Type
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <PickerToggler
                            ref={riskPickerRef}
                            labelComponent={<MultiRiskPickerPreview />}
                            icon="Charts"
                            alignItems={
                                size(selectedRisks) > 1
                                    ? 'flex-start'
                                    : 'center'
                            }
                            collapsedIcon="RightArrow"
                            trailingActions={<MultiRiskPickerClearButton />}>
                            <div
                                style={{
                                    //todo: use Absolute but fix width: 100%;
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                }}>
                                <RiskPickerIndicators
                                    multi
                                    onClose={() =>
                                        riskPickerRef.current.close()
                                    }
                                />
                            </div>
                        </PickerToggler>
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
        </FGStack>
    );
});

export default CountryRiskPicker;
